import { hotspotCopy } from "../content/site-copy";

export const HOTSPOT_IDS = ["sea", "journal", "hearth", "menu", "boards"] as const;

export type HotspotId = (typeof HOTSPOT_IDS)[number];

export type HotspotPosition = Readonly<{
  x: number;
  y: number;
  width: number;
  height: number;
}>;

export type HotspotDefinition = Readonly<{
  id: HotspotId;
  label: string;
  shortLabel: string;
  description: string;
  invitation: string;
  position: Readonly<{
    desktop: HotspotPosition;
    mobile: HotspotPosition;
  }>;
}>;

/**
 * Coordinates are percentages of the responsive scene frame. Desktop and
 * mobile deliberately use different compositions.
 *
 * Only geometry lives here. The four visible strings per hotspot are editorial
 * and come from Sanity through `content/site-copy.ts`; they are merged in
 * below so `HOTSPOTS` still reads as one complete definition.
 */
const HOTSPOT_POSITIONS: Readonly<
  Record<HotspotId, HotspotDefinition["position"]>
> = {
  sea: {
    desktop: { x: 54, y: 8, width: 18, height: 30 },
    mobile: { x: 55, y: 23, width: 18, height: 22 },
  },
  journal: {
    desktop: { x: 70, y: 72, width: 23, height: 21 },
    mobile: { x: 67, y: 70, width: 29, height: 20 },
  },
  hearth: {
    desktop: { x: 43, y: 30, width: 27, height: 43 },
    mobile: { x: 36, y: 39, width: 40, height: 34 },
  },
  menu: {
    desktop: { x: 1, y: 35, width: 23, height: 59 },
    mobile: { x: 0, y: 41, width: 22, height: 49 },
  },
  boards: {
    desktop: { x: 70, y: 10, width: 27, height: 55 },
    mobile: { x: 74, y: 26, width: 26, height: 38 },
  },
};

export const HOTSPOTS: readonly HotspotDefinition[] = HOTSPOT_IDS.map((id) => ({
  ...hotspotCopy[id],
  position: HOTSPOT_POSITIONS[id],
}));

export function getHotspot(id: HotspotId): HotspotDefinition {
  const hotspot = HOTSPOTS.find((candidate) => candidate.id === id);

  // HotspotId is derived from HOTSPOT_IDS. This guard keeps runtime failures
  // explicit if the configuration is accidentally made incomplete later.
  if (!hotspot) {
    throw new Error(`Geen hotspotconfiguratie gevonden voor: ${id}`);
  }

  return hotspot;
}
